import { X } from 'lucide-react'
import { useRef } from 'react'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DRAG_THRESHOLD, STAMP_Z_INDEX } from '../consts'
import type { CanvasStamp, CardPosition } from '../types'

type DragState = {
  pointerId: number
  startClientX: number
  startClientY: number
  originX: number
  originY: number
  moved: boolean
}

export const CanvasStampItem = ({
  stamp,
  scale,
  selected,
  onSelect,
  onMove,
  onMoveEnd,
  onDelete,
  onContextMenu,
}: {
  stamp: CanvasStamp
  scale: number
  selected: boolean
  onSelect: (opts?: { additive?: boolean }) => void
  onMove?: (position: CardPosition) => void
  onMoveEnd?: () => void
  onDelete?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}) => {
  const { t } = useLocalization()
  const dragState = useRef<DragState | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    onSelect({ additive: e.shiftKey || e.metaKey || e.ctrlKey })
    if ((e.target as HTMLElement).closest('button')) return
    if (!onMove) return
    dragState.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: stamp.x,
      originY: stamp.y,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released; drag still works via bubbling events.
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dxClient = e.clientX - drag.startClientX
    const dyClient = e.clientY - drag.startClientY
    if (!drag.moved && Math.hypot(dxClient, dyClient) > DRAG_THRESHOLD) {
      drag.moved = true
    }
    if (drag.moved && onMove) {
      onMove({
        x: drag.originX + dxClient / scale,
        y: drag.originY + dyClient / scale,
      })
    }
  }

  const endDrag = () => {
    if (dragState.current) {
      dragState.current = null
      onMoveEnd?.()
    }
  }

  return (
    <div
      className={cn(
        'absolute left-0 top-0 select-none',
        'cursor-grab active:cursor-grabbing rounded-full p-1',
        selected && 'ring-2 ring-blue-500'
      )}
      style={{
        // Second translate centers the emoji on the stamp position.
        transform: `translate(${stamp.x}px, ${stamp.y}px) translate(-50%, -50%)`,
        zIndex: STAMP_Z_INDEX,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onContextMenu={onContextMenu}
      onDragStart={(e) => e.preventDefault()}
    >
      {selected && onDelete ? (
        <button
          type="button"
          className="absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-gray-900 text-white shadow-md hover:bg-gray-700"
          onClick={onDelete}
          aria-label={t('Remove stamp', 'Ta bort stämpel')}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
      <span className="block p-1 text-4xl leading-none">{stamp.emoji}</span>
    </div>
  )
}
